/**
 * Admin: Buntu companion — all six policy areas, org messages, and a live preview.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  CheckCircle,
  Eye,
  MessageSquare,
  Save,
  Send,
  Settings2,
  Shield,
} from 'lucide-react';
import { apiService } from '../services/api';
import DevicePicker from './DevicePicker';

type InnerTab = 'policy' | 'messages' | 'preview';

interface BuntuConfig {
  enablement: {
    enabled: boolean;
    platforms: string[];
    scope: string;
    group_ids: string[];
    allow_user_opt_out: boolean;
    required_for_admins: boolean;
  };
  presence: {
    default_corner: string;
    idle_floating_head: boolean;
    cursor_watching: boolean;
    drag_and_drop_return: boolean;
    click_expressions: boolean;
    respect_reduced_motion: boolean;
  };
  wellness: {
    enabled: boolean;
    interval_minutes: number;
    message_pack: string[];
    quiet_hours_enabled: boolean;
    quiet_hours_start: string;
    quiet_hours_end: string;
    pause_when_idle: boolean;
  };
  messaging: {
    enabled: boolean;
    default_priority: string;
    default_delivery: string;
    default_sticky_minutes: number;
  };
  branding: {
    companion_name: string;
    welcome_line: string;
    welcome_note: string;
    welcome_cta_label: string;
    bubble_accent: string;
    messaging_only: boolean;
  };
  privacy: {
    cosmetic_notice: boolean;
    no_surveillance: boolean;
    allow_minimize: boolean;
    dnd_enabled: boolean;
    dnd_start: string;
    dnd_end: string;
  };
  managed_install: {
    enabled: boolean;
    notice: string;
    cta_label: string;
    cta_admin_label: string;
    ticket_subject: string;
  };
}

interface BuntuMessage {
  id: string;
  body: string;
  priority: string;
  delivery: string;
  sticky_minutes: number;
  audience?: string;
  audience_ids?: string[];
  status: string;
  sent_at?: string | null;
  created_by?: string | null;
  created_at?: string | null;
  delivered_count?: number;
  dismissed_count?: number;
}

function audienceLabel(m: BuntuMessage): string {
  switch (m.audience) {
    case 'windows':
      return 'Windows desktops only';
    case 'client_admins':
      return 'Client Admins (Hub)';
    case 'groups':
      return `Collection (${(m.audience_ids || []).length})`;
    case 'devices':
      return `Devices (${(m.audience_ids || []).length})`;
    case 'all':
    default:
      return 'Hub + Windows';
  }
}

const DEFAULT_CONFIG: BuntuConfig = {
  enablement: {
    enabled: true,
    platforms: ['hub', 'windows', 'linux', 'macos'],
    scope: 'all',
    group_ids: [],
    allow_user_opt_out: true,
    required_for_admins: false,
  },
  presence: {
    default_corner: 'bottom-right',
    idle_floating_head: true,
    cursor_watching: true,
    drag_and_drop_return: true,
    click_expressions: true,
    respect_reduced_motion: true,
  },
  wellness: {
    enabled: true,
    interval_minutes: 60,
    message_pack: [
      "That's an hour at the screen. Stand up and stretch for a minute.",
      'An hour already. Roll your shoulders, look out a window.',
      "You've been at it an hour. Give your eyes a short break.",
    ],
    quiet_hours_enabled: false,
    quiet_hours_start: '12:00',
    quiet_hours_end: '13:00',
    pause_when_idle: true,
  },
  messaging: {
    enabled: true,
    default_priority: 'info',
    default_delivery: 'once',
    default_sticky_minutes: 5,
  },
  branding: {
    companion_name: 'Buntu',
    welcome_line: 'Hi {user}, welcome to {tenant}.',
    welcome_note:
      'On a lighter note — this system is managed by your administrator. ' +
      'Please contact them if you need any assistance.',
    welcome_cta_label: 'Raise ticket',
    bubble_accent: '#253546',
    messaging_only: false,
  },
  privacy: {
    cosmetic_notice: true,
    no_surveillance: true,
    allow_minimize: true,
    dnd_enabled: false,
    dnd_start: '18:00',
    dnd_end: '09:00',
  },
  managed_install: {
    enabled: true,
    notice:
      'Your system is controlled by your system administrator. If changes are required, contact the administrator.',
    cta_label: 'Raise ticket',
    cta_admin_label: 'Use admin credentials',
    ticket_subject: 'Software install requested',
  },
};

function mergeConfig(raw: Partial<BuntuConfig> | null | undefined): BuntuConfig {
  const r = raw || {};
  return {
    enablement: { ...DEFAULT_CONFIG.enablement, ...(r.enablement || {}) },
    presence: { ...DEFAULT_CONFIG.presence, ...(r.presence || {}) },
    wellness: {
      ...DEFAULT_CONFIG.wellness,
      ...(r.wellness || {}),
      message_pack: r.wellness?.message_pack?.length
        ? r.wellness.message_pack
        : DEFAULT_CONFIG.wellness.message_pack,
    },
    messaging: { ...DEFAULT_CONFIG.messaging, ...(r.messaging || {}) },
    branding: { ...DEFAULT_CONFIG.branding, ...(r.branding || {}) },
    privacy: { ...DEFAULT_CONFIG.privacy, ...(r.privacy || {}) },
    managed_install: { ...DEFAULT_CONFIG.managed_install, ...(r.managed_install || {}) },
  };
}

const Toggle: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  desc?: string;
  disabled?: boolean;
}> = ({ checked, onChange, label, desc, disabled }) => (
  <label className={`flex items-start gap-3 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
    <input
      type="checkbox"
      className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
    />
    <span>
      <span className="block text-sm font-medium text-slate-800">{label}</span>
      {desc && <span className="block text-xs text-slate-500 mt-0.5">{desc}</span>}
    </span>
  </label>
);

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({
  title,
  subtitle,
  children,
}) => (
  <section className="rounded-lg border border-slate-200 bg-slate-50 p-5 space-y-4">
    <div>
      <h4 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">{title}</h4>
      {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
    </div>
    {children}
  </section>
);

const BuntuCompanionSettings: React.FC = () => {
  const [inner, setInner] = useState<InnerTab>('policy');
  const [config, setConfig] = useState<BuntuConfig>(DEFAULT_CONFIG);
  const [messages, setMessages] = useState<BuntuMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [compose, setCompose] = useState({
    body: '',
    priority: 'info',
    delivery: 'once',
    sticky_minutes: 5,
    audience: 'all',
    audience_ids: [] as string[],
    deviceMode: 'multiple' as 'single' | 'multiple',
    send_now: true,
  });
  const [sending, setSending] = useState(false);
  const [previewLine, setPreviewLine] = useState('Hello from your admin — this is how Buntu will speak.');
  const [devices, setDevices] = useState<any[]>([]);
  const [collections, setCollections] = useState<any[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [settingsRes, messagesRes, devicesRes, directoryRes] = await Promise.all([
        apiService.getBuntuSettings(),
        apiService.listBuntuMessages(),
        apiService.getDevices().catch(() => []),
        apiService.getTenantDirectory().catch(() => ({ folders: [], collections: [] })),
      ]);
      const cfg = settingsRes?.data?.config || settingsRes?.config || settingsRes?.data || {};
      setConfig(mergeConfig(cfg));
      const items = messagesRes?.data?.items || messagesRes?.items || [];
      setMessages(Array.isArray(items) ? items : []);
      const deviceItems = Array.isArray(devicesRes)
        ? devicesRes
        : (devicesRes as any)?.items || (devicesRes as any)?.data || [];
      setDevices(Array.isArray(deviceItems) ? deviceItems : []);
      const cols = directoryRes?.collections || (directoryRes as any)?.data?.collections || [];
      setCollections(Array.isArray(cols) ? cols : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load Buntu settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patchSection = <K extends keyof BuntuConfig>(section: K, updates: Partial<BuntuConfig[K]>) => {
    setConfig((prev) => ({
      ...prev,
      [section]: { ...prev[section], ...updates },
    }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await apiService.updateBuntuSettings(config);
      const cfg = res?.data?.config || config;
      setConfig(mergeConfig(cfg));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      window.dispatchEvent(new CustomEvent('buntu-policy-updated'));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!compose.body.trim()) {
      setError('Message body is required');
      return;
    }
    if (compose.audience === 'groups' && compose.audience_ids.length === 0) {
      setError('Select at least one collection');
      return;
    }
    if (compose.audience === 'devices' && compose.audience_ids.length === 0) {
      setError('Select at least one Windows device');
      return;
    }
    setSending(true);
    setError(null);
    try {
      await apiService.createBuntuMessage({
        body: compose.body.trim(),
        priority: compose.priority,
        delivery: compose.delivery,
        sticky_minutes: compose.sticky_minutes,
        audience: compose.audience,
        audience_ids: compose.audience_ids,
        send_now: compose.send_now,
      });
      setCompose((c) => ({ ...c, body: '', audience_ids: [] }));
      window.dispatchEvent(new CustomEvent('buntu-inbox-updated'));
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleCancel = async (id: string) => {
    try {
      await apiService.cancelBuntuMessage(id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to cancel');
    }
  };

  const platformToggle = (plat: string) => {
    const set = new Set(config.enablement.platforms);
    if (set.has(plat)) set.delete(plat);
    else set.add(plat);
    patchSection('enablement', { platforms: Array.from(set) });
  };

  const packText = useMemo(() => config.wellness.message_pack.join('\n'), [config.wellness.message_pack]);

  const tabBtn = (id: InnerTab, label: string, icon: React.ReactNode) => (
    <button
      type="button"
      onClick={() => setInner(id)}
      className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md ${
        inner === id ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
      }`}
    >
      {icon}
      {label}
    </button>
  );

  if (loading) {
    return <div className="text-sm text-slate-500 py-6">Loading Buntu settings…</div>;
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Settings2 size={20} className="text-blue-600" />
            Desktop character
          </h3>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Policy, wellness, branding, and broadcast messages for the floating companion on Windows.
            Use Companion portal mode for the web companion inbox.
          </p>
        </div>
        {inner === 'policy' && (
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
          >
            <Save size={16} />
            {saving ? 'Saving…' : 'Save policy'}
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {tabBtn('policy', 'Policy', <Shield size={16} />)}
        {tabBtn('messages', 'Messages', <MessageSquare size={16} />)}
        {tabBtn('preview', 'Preview', <Eye size={16} />)}
      </div>

      {saved && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm">
          <CheckCircle size={18} />
          Policy saved. Hub companions will pick it up shortly.
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {inner === 'policy' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Section title="1. Enablement" subtitle="Master switch and where Buntu may appear.">
            <Toggle
              checked={config.enablement.enabled}
              onChange={(v) => patchSection('enablement', { enabled: v })}
              label="Enable Buntu for this tenant"
              desc="Turns the companion off for the whole organisation when unchecked"
            />
            <div>
              <div className="text-sm font-medium text-slate-800 mb-2">Platforms</div>
              <div className="flex flex-wrap gap-3">
                {[
                  ['hub', 'Hub UI'],
                  ['windows', 'Windows'],
                  ['linux', 'Linux'],
                  ['macos', 'macOS'],
                ].map(([id, label]) => (
                  <label key={id} className="inline-flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={config.enablement.platforms.includes(id)}
                      onChange={() => platformToggle(id)}
                      className="h-4 w-4 rounded border-slate-300 text-blue-600"
                    />
                    {label}
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Desktop companions ship with the agent later; Hub UI is live today.
              </p>
            </div>
            <Toggle
              checked={config.enablement.allow_user_opt_out}
              onChange={(v) => patchSection('enablement', { allow_user_opt_out: v })}
              label="Allow user opt-out"
              desc="Users can minimize / snooze from the companion menu"
            />
            <Toggle
              checked={config.enablement.required_for_admins}
              onChange={(v) => patchSection('enablement', { required_for_admins: v })}
              label="Required for Client Admin Hub logins"
              desc="Tenant admins cannot minimize or hide Buntu in the browser"
            />
          </Section>

          <Section title="2. Presence & behaviour" subtitle="How the pet behaves when idle and when touched.">
            <div>
              <label className="text-sm font-medium text-slate-800">Default corner</label>
              <select
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                value={config.presence.default_corner}
                onChange={(e) => patchSection('presence', { default_corner: e.target.value })}
              >
                <option value="bottom-right">Bottom right</option>
                <option value="bottom-left">Bottom left</option>
                <option value="top-right">Top right</option>
                <option value="top-left">Top left</option>
              </select>
            </div>
            <Toggle
              checked={config.presence.idle_floating_head}
              onChange={(v) => patchSection('presence', { idle_floating_head: v })}
              label="Idle floating head"
            />
            <Toggle
              checked={config.presence.cursor_watching}
              onChange={(v) => patchSection('presence', { cursor_watching: v })}
              label="Cursor watching"
              desc="Eyes follow the pointer"
            />
            <Toggle
              checked={config.presence.drag_and_drop_return}
              onChange={(v) => patchSection('presence', { drag_and_drop_return: v })}
              label="Drag & drop return"
              desc="Climb down, then run home to the corner"
            />
            <Toggle
              checked={config.presence.click_expressions}
              onChange={(v) => patchSection('presence', { click_expressions: v })}
              label="Click expressions"
            />
            <Toggle
              checked={config.presence.respect_reduced_motion}
              onChange={(v) => patchSection('presence', { respect_reduced_motion: v })}
              label="Respect prefers-reduced-motion"
            />
          </Section>

          <Section title="3. Wellness / stretch break" subtitle="Hourly (or custom) active-time reminders.">
            <Toggle
              checked={config.wellness.enabled}
              onChange={(v) => patchSection('wellness', { enabled: v })}
              label="Enable stretch reminders"
              disabled={config.branding.messaging_only}
            />
            <div>
              <label className="text-sm font-medium text-slate-800">Interval (minutes)</label>
              <input
                type="number"
                min={5}
                max={240}
                className="mt-1 block w-40 rounded-md border border-slate-300 px-3 py-2 text-sm"
                value={config.wellness.interval_minutes}
                onChange={(e) =>
                  patchSection('wellness', {
                    interval_minutes: Math.max(5, Math.min(240, Number(e.target.value) || 60)),
                  })
                }
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-800">Message pack (one line each)</label>
              <textarea
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono min-h-[96px]"
                value={packText}
                onChange={(e) =>
                  patchSection('wellness', {
                    message_pack: e.target.value
                      .split('\n')
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
              />
            </div>
            <Toggle
              checked={config.wellness.quiet_hours_enabled}
              onChange={(v) => patchSection('wellness', { quiet_hours_enabled: v })}
              label="Quiet hours"
            />
            {config.wellness.quiet_hours_enabled && (
              <div className="flex gap-3">
                <div>
                  <label className="text-xs text-slate-500">Start</label>
                  <input
                    type="time"
                    className="mt-1 block rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                    value={config.wellness.quiet_hours_start}
                    onChange={(e) => patchSection('wellness', { quiet_hours_start: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">End</label>
                  <input
                    type="time"
                    className="mt-1 block rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                    value={config.wellness.quiet_hours_end}
                    onChange={(e) => patchSection('wellness', { quiet_hours_end: e.target.value })}
                  />
                </div>
              </div>
            )}
            <Toggle
              checked={config.wellness.pause_when_idle}
              onChange={(v) => patchSection('wellness', { pause_when_idle: v })}
              label="Pause when user is idle / tab hidden"
            />
          </Section>

          <Section title="4. Admin messaging defaults" subtitle="Defaults for organisation broadcasts.">
            <Toggle
              checked={config.messaging.enabled}
              onChange={(v) => patchSection('messaging', { enabled: v })}
              label="Allow admin messages via Buntu"
            />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-800">Default priority</label>
                <select
                  className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  value={config.messaging.default_priority}
                  onChange={(e) => patchSection('messaging', { default_priority: e.target.value })}
                >
                  <option value="info">Info</option>
                  <option value="important">Important</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-800">Default delivery</label>
                <select
                  className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  value={config.messaging.default_delivery}
                  onChange={(e) => patchSection('messaging', { default_delivery: e.target.value })}
                >
                  <option value="once">Show once</option>
                  <option value="until_dismissed">Until dismissed</option>
                  <option value="sticky">Sticky (minutes)</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-800">Default sticky minutes</label>
              <input
                type="number"
                min={1}
                max={240}
                className="mt-1 block w-32 rounded-md border border-slate-300 px-3 py-2 text-sm"
                value={config.messaging.default_sticky_minutes}
                onChange={(e) =>
                  patchSection('messaging', {
                    default_sticky_minutes: Math.max(1, Math.min(240, Number(e.target.value) || 5)),
                  })
                }
              />
            </div>
          </Section>

          <Section
            title="Managed PC notice"
            subtitle="Shown on Windows when someone tries to install software themselves (not Hub Apps & Deployment)."
          >
            <Toggle
              checked={config.managed_install.enabled}
              onChange={(v) => patchSection('managed_install', { enabled: v })}
              label="Show administrator notice when a user starts an installer"
              desc="Hub-deployed apps still install silently as the UEM Agent (SYSTEM). Local admins may still see UAC."
            />
            <div>
              <label className="text-sm font-medium text-slate-800">Notice text</label>
              <textarea
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                rows={3}
                value={config.managed_install.notice}
                onChange={(e) => patchSection('managed_install', { notice: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-800">Use admin credentials label</label>
                <input
                  className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  value={config.managed_install.cta_admin_label}
                  onChange={(e) => patchSection('managed_install', { cta_admin_label: e.target.value })}
                />
                <p className="mt-1 text-xs text-slate-500">
                  UEM Hub tenant admin (email + password), not Windows local Administrator. Queues a SYSTEM install of the detected setup file.
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-800">Raise ticket label</label>
                <input
                  className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  value={config.managed_install.cta_label}
                  onChange={(e) => patchSection('managed_install', { cta_label: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-slate-800">Ticket subject</label>
                <input
                  className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  value={config.managed_install.ticket_subject}
                  onChange={(e) => patchSection('managed_install', { ticket_subject: e.target.value })}
                />
              </div>
            </div>
          </Section>

          <Section title="5. Branding" subtitle="Name, welcome line, and accent.">
            <div>
              <label className="text-sm font-medium text-slate-800">Companion name</label>
              <input
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                value={config.branding.companion_name}
                onChange={(e) => patchSection('branding', { companion_name: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-800">Welcome line</label>
              <input
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                value={config.branding.welcome_line}
                onChange={(e) => patchSection('branding', { welcome_line: e.target.value })}
              />
              <p className="mt-1 text-xs text-slate-500">
                Greets the user once each time the companion starts.{' '}
                <code>{'{user}'}</code> and <code>{'{tenant}'}</code> are filled in.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-800">Welcome note</label>
              <textarea
                rows={2}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                value={config.branding.welcome_note}
                onChange={(e) => patchSection('branding', { welcome_note: e.target.value })}
              />
              <p className="mt-1 text-xs text-slate-500">
                Second line of the greeting. Leave blank to show only the welcome line.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-800">Welcome button</label>
              <input
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                value={config.branding.welcome_cta_label}
                onChange={(e) => patchSection('branding', { welcome_cta_label: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-800">Bubble accent</label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="color"
                  value={config.branding.bubble_accent}
                  onChange={(e) => patchSection('branding', { bubble_accent: e.target.value })}
                  className="h-9 w-12 rounded border border-slate-300"
                />
                <input
                  className="block w-36 rounded-md border border-slate-300 px-3 py-2 text-sm font-mono"
                  value={config.branding.bubble_accent}
                  onChange={(e) => patchSection('branding', { bubble_accent: e.target.value })}
                />
              </div>
            </div>
            <Toggle
              checked={config.branding.messaging_only}
              onChange={(v) => patchSection('branding', { messaging_only: v })}
              label="Messaging only"
              desc="Disable wellness pet behaviour; keep announcer / presence"
            />
          </Section>

          <Section title="6. Privacy & manners" subtitle="Keep Buntu unmistakably cosmetic.">
            <Toggle
              checked={config.privacy.cosmetic_notice}
              onChange={(v) => patchSection('privacy', { cosmetic_notice: v })}
              label="Show cosmetic-only notice"
              desc="Users see that Buntu is not monitoring their screen"
            />
            <Toggle
              checked={config.privacy.no_surveillance}
              onChange={(v) => patchSection('privacy', { no_surveillance: v })}
              label="No surveillance features"
              desc="Policy flag — no keylogging / screen capture via companion"
              disabled
            />
            <Toggle
              checked={config.privacy.allow_minimize}
              onChange={(v) => patchSection('privacy', { allow_minimize: v })}
              label="Allow minimize (restore chip)"
              disabled={!config.enablement.allow_user_opt_out}
            />
            <Toggle
              checked={config.privacy.dnd_enabled}
              onChange={(v) => patchSection('privacy', { dnd_enabled: v })}
              label="Do-not-disturb window"
            />
            {config.privacy.dnd_enabled && (
              <div className="flex gap-3">
                <div>
                  <label className="text-xs text-slate-500">Start</label>
                  <input
                    type="time"
                    className="mt-1 block rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                    value={config.privacy.dnd_start}
                    onChange={(e) => patchSection('privacy', { dnd_start: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">End</label>
                  <input
                    type="time"
                    className="mt-1 block rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                    value={config.privacy.dnd_end}
                    onChange={(e) => patchSection('privacy', { dnd_end: e.target.value })}
                  />
                </div>
              </div>
            )}
          </Section>
        </div>
      )}

      {inner === 'messages' && (
        <div className="grid gap-5 lg:grid-cols-5">
          <div className="lg:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-5 space-y-4">
            <h4 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">Compose</h4>
            <p className="text-xs text-slate-500">
              Target Hub users, all Windows desktops, a Directory collection, or specific devices.
              Device-targeted messages reach Windows companions when the UEM Agent is online
              (about every 15–30 seconds). Hub-only options never appear on the desktop.
            </p>
            <textarea
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm min-h-[120px]"
              placeholder="e.g. Network maintenance tonight at 10pm — save your work."
              value={compose.body}
              maxLength={500}
              onChange={(e) => setCompose((c) => ({ ...c, body: e.target.value }))}
              disabled={!config.messaging.enabled}
            />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500">Priority</label>
                <select
                  className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                  value={compose.priority}
                  onChange={(e) => setCompose((c) => ({ ...c, priority: e.target.value }))}
                >
                  <option value="info">Info</option>
                  <option value="important">Important</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500">Delivery</label>
                <select
                  className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                  value={compose.delivery}
                  onChange={(e) => setCompose((c) => ({ ...c, delivery: e.target.value }))}
                >
                  <option value="once">Show once</option>
                  <option value="until_dismissed">Until dismissed</option>
                  <option value="sticky">Sticky</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-500">Audience</label>
              <select
                className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                value={compose.audience}
                onChange={(e) =>
                  setCompose((c) => ({
                    ...c,
                    audience: e.target.value,
                    audience_ids: [],
                  }))
                }
              >
                <option value="all">All Hub users + Windows desktops</option>
                <option value="windows">Windows desktops only</option>
                <option value="client_admins">Client Admins only (Hub)</option>
                <option value="groups">Directory collection</option>
                <option value="devices">Specific Windows device(s)</option>
              </select>
            </div>

            {compose.audience === 'groups' && (
              <div>
                <label className="text-xs text-slate-500">Collection</label>
                <select
                  className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                  value={compose.audience_ids[0] || ''}
                  onChange={(e) =>
                    setCompose((c) => ({
                      ...c,
                      audience_ids: e.target.value ? [e.target.value] : [],
                    }))
                  }
                >
                  <option value="">Select a collection…</option>
                  {collections.map((col) => (
                    <option key={col.id} value={col.id}>
                      {col.name}
                      {Array.isArray(col.deviceIds) ? ` (${col.deviceIds.length})` : ''}
                    </option>
                  ))}
                </select>
                {collections.length === 0 && (
                  <p className="mt-1 text-[11px] text-amber-700">
                    No collections yet — create one under Directory first.
                  </p>
                )}
              </div>
            )}

            {compose.audience === 'devices' && (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setCompose((c) => ({ ...c, deviceMode: 'single', audience_ids: [] }))
                    }
                    className={`flex-1 text-xs py-1.5 rounded border ${
                      compose.deviceMode === 'single'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-slate-600 border-slate-300'
                    }`}
                  >
                    Individual
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setCompose((c) => ({ ...c, deviceMode: 'multiple', audience_ids: [] }))
                    }
                    className={`flex-1 text-xs py-1.5 rounded border ${
                      compose.deviceMode === 'multiple'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-slate-600 border-slate-300'
                    }`}
                  >
                    Multiple
                  </button>
                </div>
                {compose.deviceMode === 'single' ? (
                  <DevicePicker
                    mode="single"
                    devices={devices}
                    platformFilter="windows"
                    value={compose.audience_ids[0] || ''}
                    onChange={(id) =>
                      setCompose((c) => ({
                        ...c,
                        audience_ids: id ? [id] : [],
                      }))
                    }
                    placeholder="Search Windows devices…"
                    emptyMessage="No Windows devices found"
                  />
                ) : (
                  <DevicePicker
                    mode="multiple"
                    devices={devices}
                    platformFilter="windows"
                    value={compose.audience_ids}
                    onChange={(ids) => setCompose((c) => ({ ...c, audience_ids: ids }))}
                    placeholder="Search and select Windows devices…"
                    emptyMessage="No Windows devices found"
                    maxHeightClass="max-h-40"
                  />
                )}
              </div>
            )}

            {compose.delivery === 'sticky' && (
              <div>
                <label className="text-xs text-slate-500">Sticky minutes</label>
                <input
                  type="number"
                  min={1}
                  max={240}
                  className="mt-1 block w-28 rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                  value={compose.sticky_minutes}
                  onChange={(e) =>
                    setCompose((c) => ({
                      ...c,
                      sticky_minutes: Math.max(1, Math.min(240, Number(e.target.value) || 5)),
                    }))
                  }
                />
              </div>
            )}
            <button
              type="button"
              onClick={handleSend}
              disabled={sending || !config.messaging.enabled || !config.enablement.enabled}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
            >
              <Send size={16} />
              {sending ? 'Sending…' : 'Send message'}
            </button>
          </div>

          <div className="lg:col-span-3 rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h4 className="text-sm font-semibold text-slate-800">History</h4>
              <button type="button" onClick={load} className="text-xs text-blue-600 hover:underline">
                Refresh
              </button>
            </div>
            <div className="divide-y divide-slate-100 max-h-[480px] overflow-y-auto">
              {messages.length === 0 && (
                <div className="p-6 text-sm text-slate-500">No messages yet.</div>
              )}
              {messages.map((m) => (
                <div key={m.id} className="p-4 flex gap-3 items-start">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span
                        className={`text-[11px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                          m.priority === 'urgent'
                            ? 'bg-red-100 text-red-700'
                            : m.priority === 'important'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {m.priority}
                      </span>
                      <span className="text-[11px] text-slate-500">{m.status}</span>
                      <span className="text-[11px] text-slate-500">{audienceLabel(m)}</span>
                      <span className="text-[11px] text-slate-400">
                        seen {m.delivered_count ?? 0} · dismissed {m.dismissed_count ?? 0}
                      </span>
                    </div>
                    <p className="text-sm text-slate-800 whitespace-pre-wrap">{m.body}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {m.created_by || 'admin'}
                      {m.sent_at ? ` · ${new Date(m.sent_at).toLocaleString()}` : ''}
                    </p>
                  </div>
                  {(m.status === 'sent' || m.status === 'scheduled' || m.status === 'draft') && (
                    <button
                      type="button"
                      onClick={() => handleCancel(m.id)}
                      className="text-xs text-slate-500 hover:text-red-600 shrink-0"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {inner === 'preview' && (
        <div className="rounded-lg border border-slate-200 bg-gradient-to-br from-slate-100 to-slate-200 p-6 min-h-[320px] relative overflow-hidden">
          <div className="absolute top-4 left-4 space-y-1 max-w-sm">
            <p className="text-sm font-semibold text-slate-800">
              {config.branding.companion_name || 'Buntu'} preview
            </p>
            <p className="text-xs text-slate-600">
              {config.enablement.enabled
                ? `Enabled · corner ${config.presence.default_corner} · wellness ${
                    config.wellness.enabled && !config.branding.messaging_only
                      ? `every ${config.wellness.interval_minutes}m`
                      : 'off'
                  }`
                : 'Disabled for this tenant'}
            </p>
            {config.privacy.cosmetic_notice && (
              <p className="text-xs text-slate-500 italic">
                Cosmetic companion — not monitoring your screen or keystrokes.
              </p>
            )}
          </div>
          <div className="absolute bottom-6 right-8 flex flex-col items-end gap-2">
            <div
              className="max-w-[240px] rounded-xl bg-white px-3 py-2 text-xs shadow-lg"
              style={{ color: config.branding.bubble_accent || '#253546' }}
            >
              {previewLine || config.branding.welcome_line}
            </div>
            <img
              src={`${process.env.PUBLIC_URL || ''}/buntu/head.png`}
              alt={config.branding.companion_name || 'Buntu'}
              className="w-[110px] h-auto drop-shadow-lg select-none"
              draggable={false}
            />
          </div>
          <div className="absolute bottom-4 left-4 flex gap-2 items-end">
            <input
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm w-72 bg-white/90"
              value={previewLine}
              onChange={(e) => setPreviewLine(e.target.value)}
              placeholder="Preview bubble text"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BuntuCompanionSettings;
